/*
 * M75 — Back-in-Stock variant injector for Shopify Forms widget.
 *
 * Pairs with sections/bis-variant-injector.liquid. Three responsibilities:
 *
 *   1. Toggle `body.bis-variant-oos` based on the currently-selected variant's
 *      availability. Section CSS hides the Forms inline-block by default and
 *      reveals it only when the body class is present, so the BIS form only
 *      shows up on OOS variants without requiring a page reload after a
 *      variant switch.
 *
 *   2. Watch the DOM (and Shopify Forms's open shadow roots) for the
 *      Forms-rendered <input name="bis_variant_id">, auto-fill it with the
 *      current PDP variant ID merged with the visitor's prior in-flight
 *      subscriptions stored in localStorage, and persist the merged CSV
 *      back on submit.
 *
 *   3. Inject a hide stylesheet into each Forms shadow root so the field
 *      is invisible to the customer. Light-DOM CSS can't penetrate the
 *      Forms widget's shadow root — has to be done from inside.
 *
 * Forms widget architecture: Shopify Forms renders inline forms into a
 * <shopify-forms-embed> custom element with mode:'open' shadow DOM (per
 * shopify-forms-loader.js source). All form fields live inside that shadow
 * root, so CSS rules in the document or section <style> tags do nothing.
 *
 * The customer metafield (custom.bis_variant_id) only holds a single value,
 * so to support a customer subscribing to multiple variants across PDPs in
 * one browsing session, we stage the merged CSV client-side. Each form
 * submit overwrites the metafield with the merged value.
 *
 * See: app_suite_apis/services/shopify/modules/cronCheckBackInStockMetafields.js
 * for the polling cron that drains the metafield + removes the
 * BIS-Subscriber tag once variants restock.
 */

(function () {
    'use strict';

    var configEl = document.getElementById('bis-injector-config');
    if (!configEl) return;

    var config;
    try {
        config = JSON.parse(configEl.textContent || configEl.innerText);
    } catch (e) {
        return;
    }

    var inputName = config.metafieldInputName || 'bis_variant_id';
    var maxVariants = parseInt(config.maxVariants, 10) || 12;
    var storageKey = config.storageKey || 'bis_subs_pending';
    var variantsAvailability = (config.variantsAvailability && typeof config.variantsAvailability === 'object')
        ? config.variantsAvailability
        : {};

    var currentVariantId = String(config.currentVariantId || '');
    var currentVariantAvailable = !!config.currentVariantAvailable;

    // Track shadow roots we've already wired observers + hide styles into.
    var watchedShadowRoots = new WeakSet();

    // ----- OOS gate -----

    function applyOosGate() {
        var available = (variantsAvailability[currentVariantId] !== undefined)
            ? !!variantsAvailability[currentVariantId]
            : currentVariantAvailable;
        document.body.classList.toggle('bis-variant-oos', !available);
    }

    // ----- localStorage CSV staging -----

    function readStoredCsv() {
        try {
            var raw = window.localStorage.getItem(storageKey) || '';
            return parseCsv(raw);
        } catch (e) {
            return [];
        }
    }

    function writeStoredCsv(arr) {
        try {
            window.localStorage.setItem(storageKey, arr.join(','));
        } catch (e) {
            // localStorage may be disabled — soft fail.
        }
    }

    function parseCsv(raw) {
        if (typeof raw !== 'string' || !raw) return [];
        return raw.split(',')
            .map(function (s) { return s.trim(); })
            .filter(function (s) { return /^\d{6,19}$/.test(s); });
    }

    function dedupCap(arr) {
        var seen = {};
        var out = [];
        for (var i = 0; i < arr.length; i += 1) {
            if (!seen[arr[i]]) {
                seen[arr[i]] = true;
                out.push(arr[i]);
                if (out.length >= maxVariants) break;
            }
        }
        return out;
    }

    function buildMergedCsv() {
        var stored = readStoredCsv();
        var merged = stored.slice();
        if (currentVariantId && /^\d{6,19}$/.test(currentVariantId)) {
            merged.push(currentVariantId);
        }
        return dedupCap(merged);
    }

    // ----- Shadow-DOM-aware input discovery -----

    /**
     * Compose the CSS attribute selector that matches the bis_variant_id
     * input under whatever attribute Forms uses. As of Forms 2393 (May 2026)
     * the rendered input has no `name`, only `id="custom#bis_variant_id"`,
     * `data-testid="field-custom#bis_variant_id"`, and
     * `aria-labelledby="label-custom#bis_variant_id"`. Match all three so
     * we're robust to future Forms changes.
     */
    function inputSelector() {
        return 'input[id*="' + inputName + '"], '
            + 'input[data-testid*="' + inputName + '"], '
            + 'input[name="' + inputName + '"]';
    }

    /**
     * Composite selector that matches the input AND its labelledby label
     * AND any wrapper whose id contains the field name. Used by the shadow-
     * root <style> injection.
     */
    function hideSelector() {
        return inputSelector()
            + ', [id*="' + inputName + '"]'
            + ', *:has(> input[id*="' + inputName + '"]):not(form):not(:host)'
            + ', *:has(> input[data-testid*="' + inputName + '"]):not(form):not(:host)';
    }

    /**
     * Find all bis_variant_id inputs across light DOM AND any open shadow
     * roots on the page. Forms uses <shopify-forms-embed> with mode:'open'
     * so we can traverse them.
     */
    function findInputs() {
        var sel = inputSelector();
        var inputs = [];

        Array.prototype.push.apply(inputs, document.querySelectorAll(sel));

        var hosts = document.querySelectorAll('shopify-forms-embed');
        for (var i = 0; i < hosts.length; i += 1) {
            var sr = hosts[i].shadowRoot;
            if (!sr) continue;
            Array.prototype.push.apply(inputs, sr.querySelectorAll(sel));
        }

        return inputs;
    }

    /**
     * Inject a <style> into a shadow root (once) that hides the
     * bis_variant_id input + its associated label + its wrapper. Light-DOM
     * CSS can't reach inside, so we have to plant the rule from within.
     */
    function injectShadowHideStyle(shadowRoot) {
        if (!shadowRoot || shadowRoot.querySelector('style[data-bis-hide]')) return;
        var style = document.createElement('style');
        style.setAttribute('data-bis-hide', '1');
        style.textContent = hideSelector() + ' { display: none !important; visibility: hidden !important; height: 0 !important; padding: 0 !important; margin: 0 !important; overflow: hidden !important; }';
        shadowRoot.appendChild(style);
    }

    /**
     * Walk up to the closest plausible field wrapper and force display:none
     * directly on the element (belt-and-suspenders on top of the CSS rule).
     */
    function hideInputAndWrapper(input) {
        // The label associated via aria-labelledby (e.g. id="label-custom#bis_variant_id").
        var labelId = input.getAttribute('aria-labelledby');
        if (labelId) {
            var rootForLookup = input.getRootNode();
            // CSS.escape is needed because the id contains a `#` separator.
            try {
                var label = rootForLookup.querySelector('#' + (window.CSS && CSS.escape ? CSS.escape(labelId) : labelId));
                if (label) label.style.display = 'none';
            } catch (e) { /* selector parse failure → skip */ }
        }

        var wrapper = input.closest('.shopify-forms-field, .shopify-forms__field, label, [class*="field"], [class*="form-field"], [class*="formInputField"]')
            || input.parentElement;
        if (wrapper && wrapper !== input.form) {
            wrapper.style.display = 'none';
        }
        input.style.display = 'none';
    }

    /**
     * Set an input's value in a way that React/Vue/etc. will pick up.
     * Direct `input.value = x` doesn't notify the framework's controlled-
     * input tracker; the native setter via Object.getOwnPropertyDescriptor
     * does. Forms is built on React (per shopify-forms internals) so we
     * need this for the framework's internal state to track the change.
     */
    function setReactInputValue(input, value) {
        var proto = Object.getPrototypeOf(input);
        var descriptor = Object.getOwnPropertyDescriptor(proto, 'value')
            || Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
        if (descriptor && descriptor.set) {
            descriptor.set.call(input, value);
        } else {
            input.value = value;
        }
    }

    function applyToInput(input) {
        if (!input) return;
        hideInputAndWrapper(input);

        var merged = buildMergedCsv();
        var newValue = merged.join(',');
        if (input.value !== newValue) {
            setReactInputValue(input, newValue);
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    function wireFormSubmit(form) {
        if (!form || form.dataset.bisSubmitWired === '1') return;
        form.dataset.bisSubmitWired = '1';
        form.addEventListener('submit', function () {
            var sel = inputSelector();
            var input = form.querySelector(sel)
                || (form.getRootNode() instanceof ShadowRoot
                    ? form.getRootNode().querySelector(sel)
                    : null);
            if (!input) return;
            var arr = parseCsv(input.value);
            writeStoredCsv(dedupCap(arr));
        }, true);
    }

    function injectAll() {
        // Plant hide-style into every shopify-forms-embed shadow root we see.
        var hosts = document.querySelectorAll('shopify-forms-embed');
        for (var h = 0; h < hosts.length; h += 1) {
            var sr = hosts[h].shadowRoot;
            if (!sr) continue;
            injectShadowHideStyle(sr);
            // Also start observing this shadow root for late-arriving fields.
            if (!watchedShadowRoots.has(sr)) {
                watchedShadowRoots.add(sr);
                var observer = new MutationObserver(function () { injectAll(); });
                observer.observe(sr, { childList: true, subtree: true });
            }
        }

        var inputs = findInputs();
        for (var i = 0; i < inputs.length; i += 1) {
            var input = inputs[i];
            if (input.dataset.bisInjected === '1') continue;
            input.dataset.bisInjected = '1';
            applyToInput(input);
            var form = input.closest('form');
            if (form) wireFormSubmit(form);
        }
    }

    function setupVariantChangeListeners() {
        document.addEventListener('change', function (e) {
            var target = e.target;
            if (!target || !target.matches) return;
            var isVariantPicker = target.matches(
                'input[name="id"], select[name="id"], '
                + 'variant-radios input[type="radio"], '
                + 'variant-selects input[type="radio"], '
                + 'variant-radios select, '
                + 'variant-selects select'
            );
            if (!isVariantPicker) return;
            var newId = target.value
                || (target.options
                    && target.options[target.selectedIndex]
                    && target.options[target.selectedIndex].value);
            if (newId && /^\d{6,19}$/.test(String(newId))) {
                currentVariantId = String(newId);
                applyOosGate();
                // Reset injection flags so next pass picks up the new variant.
                var inputs = findInputs();
                for (var i = 0; i < inputs.length; i += 1) {
                    inputs[i].dataset.bisInjected = '';
                }
                injectAll();
            }
        }, true);
    }

    function start() {
        applyOosGate();
        injectAll();

        // Light-DOM observer catches the <shopify-forms-embed> host being
        // added to the page. Once attached, we'll observe its shadow root
        // separately (see injectAll).
        var lightObserver = new MutationObserver(function () { injectAll(); });
        lightObserver.observe(document.body || document.documentElement, {
            childList: true,
            subtree: true,
        });

        setupVariantChangeListeners();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }
}());
